import { action, computed, IObservableArray, IObservableValue, IObservableObject, observable, toJS } from 'mobx';
import { calculatePriceOfItemByVouchers } from 'helpers/src/vouchers/calculatePriceOfItemByVouchers';
import { fetchStaticDiscounts } from '../domain/fetchStaticDiscounts';
import { isVoucherApplicable } from 'helpers/src/vouchers/isVoucherApplicable';
import { findFinalPrice, findPrice } from '../helpers/prices';
import CountryService from './CountryService';
import { getDeliveryMethodPrices } from '../helpers/deliveryMethods/getDeliveryMethodPrices';
import { canUseWindow } from '../domain/ssrTools';
import { roundPrice } from '../helpers/prices/roundPrice';
import AuthService from './AuthService';
import { ApolloClientFactory } from '../domain/apolloClientFactory';
import { GetFullVariantsQuery } from '../components/Query/Products/FullVariants';
import FlashStore from './FlashStore';
import GoogleTagManagerService from './GoogleTagManagerService';

const PRODUCT_STATUS_AVAILABLE = 'Skladem';
const PRODUCT_STATUS_UNAVAILABLE = 'Nedostupné';
const COUPON_TYPE = 'coupon';

const isCoupon = (v: any) => v.voucherGroup.type === COUPON_TYPE;
const haveValidCustomer = (v: any) => {
  const { customer } = AuthService.GetInstance();
  return v.customerId ? (customer && customer.id) === v.customerId : true;
};
export interface IOrder {
  variant: GQL.IProductVariant;
  quantity: number;
  GTMItemListName: string | undefined;
}

interface IMaxQuantityType {
  value: number | null;
  type: 'STOCKQUANTITY' | 'PURCHASELIMITQUANTITY';
}

export default class CartService {
  private static _instance: CartService;

  @observable
  private _orderedItems: IObservableArray<IOrder>;

  @observable
  private _delivery: IObservableValue<GQL.IDeliveryMethod | null>;

  @observable
  private _deliveryInsuranceLimit: IObservableValue<GQL.IDeliveryMethodInsuranceLimit | null>;

  @observable
  private _payment: IObservableValue<GQL.IPaymentMethod | null>;

  @observable
  private _customerData: IObservableObject;

  @observable
  private _dynamicVouchers: Array<GQL.IVoucher>;

  @observable
  private _staticVouchers: Array<GQL.IVoucher>;

  @observable
  private _recycledBox: boolean;

  public static GetInstance() {
    if (this._instance == undefined) {
      this._instance = new CartService();
    }

    return this._instance;
  }

  @computed
  public get orderItems() {
    return this._orderedItems;
  }

  @computed
  public get voucherTitle() {
    const voucher = this._dynamicVouchers.find(v => v.title != undefined);
    const voucherGroupTitle = voucher && voucher.voucherGroup && voucher && voucher.voucherGroup.title;

    if (voucher != undefined) {
      return `${voucher.title} - ${voucherGroupTitle}`;
    }

    return undefined;
  }

  @computed
  public get vouchers() {
    return [...this._dynamicVouchers || [], ...this._staticVouchers || []];
  }

  @computed
  public get voucherIds() {
    return this.vouchers.map(v => v.id);
  }

  public isEmpty() {
    return (this.getAllVariantIds().length === 0);
  }

  public isFreeDelivery(deliveryId: string) {
    const validVouchers = this.getValidVouchers(this._orderedItems);

    const extendedValidVouchers = [...validVouchers, ...(this.vouchers.filter(v => v.voucherGroup && v.voucherGroup.customerIds || v.customerId))];

    return extendedValidVouchers.some(v => v.voucherGroup.freeShipping.some(f => f.id == deliveryId));
  }

  public isFreeDeliveryAvailable(optionalOrder?: IOrder) {
    const validVouchers = this.getValidVouchers(optionalOrder ? [...this._orderedItems, optionalOrder] : this._orderedItems);

    const extendedValidVouchers = [...validVouchers, ...(this.vouchers.filter(v => v.voucherGroup && v.voucherGroup.customerIds || v.customerId))];

    return extendedValidVouchers.some(v => (v.voucherGroup.freeShipping || []).length > 0);
  }

  private getValidVouchers(orderedItems: Array<IOrder>) {
    const { VATrate, currency } = CountryService.GetInstance();
    const { customerGroupId } = AuthService.GetInstance();
    const validVouchers = new Array<GQL.IVoucher>();

    for (const voucher of this.vouchers) {
      let valid = false;

      for (const order of orderedItems) {
        if (
          isVoucherApplicable({
            variantId: order.variant.id,
            price: findFinalPrice(order.variant, false, VATrate),
            recommendedPrice: findFinalPrice(order.variant, true, VATrate),
            tags: order.variant.product.tags || [''], // todo: odstraň ['']
          },
            voucher.voucherGroup as any,
            this.totalPriceBeforeDiscounts(orderedItems),
            currency.id,
            customerGroupId,
          )
        ) {
          valid = true;
          break;
        }
      }

      if (valid) {
        validVouchers.push(voucher);
      }
    }

    return validVouchers;
  }

  @computed
  public get totalPrice() {
    let total = 0;

    for (const order of this._orderedItems) {
      total += this.getTotalPriceForVariant(order.variant.id, true);
    }

    return total;
  }

  @computed
  public get totalPriceWithIndividualCustomerVoucherDiscount() {
    return this.totalPrice - this.individualCustomerVoucherDiscount;
  }

  @computed
  public get totalPriceWithoutVat() {
    let total = 0;

    for (const order of this._orderedItems) {
      total += this.getTotalPriceWithoutVatForVariant(order.variant.id, true);
    }

    return total;
  }

  @computed
  public get totalPriceWithoutVatWithIndividualCustomerVoucherDiscount() {
    return this.totalPriceWithoutVat - this.individualCustomerVoucherDiscountWithoutVat;
  }

  @computed
  public get totalPriceBeforeDiscount() {
    return this.totalPriceBeforeDiscounts(this._orderedItems);
  }

  private totalPriceBeforeDiscounts(orderItems: Array<IOrder>) {
    const { VATrate } = CountryService.GetInstance();

    let total = 0;
    for (const order of orderItems) {
      total += findFinalPrice(order.variant, false, VATrate) * order.quantity;
    }

    return total;
  }

  @computed
  public get totalQuantity() {
    let total = 0;

    for (const order of this._orderedItems) {
      total += order.quantity;
    }

    return total;
  }

  @computed
  public get totalWeight() {
    let weight = 0;

    for (const order of this._orderedItems) {
      if (order.variant && order.variant.product) {
        const { product } = order.variant;

        if (product.weight) {
          weight += product.weight * order.quantity;
        }
      }
    }

    return weight;
  }

  @computed
  public get totalWeightForShipping() {
    let weight = 0;

    for (const order of this._orderedItems) {
      if (order.variant && order.variant.product) {
        const { product } = order.variant;

        if (product.weightForShipping) {
          weight += product.weightForShipping * order.quantity;
        } else if (product.weight) {
          weight += product.weight * order.quantity;
        }
      }
    }

    return weight;
  }

  @action
  public setVoucher(voucher: GQL.IVoucher, gift: GQL.IProductVariant | null) {
    if (gift) {
      const quantity = this._orderedItems.reduce((acc: number, item: IOrder) => {
        const included = Array.isArray(voucher.voucherGroup.includedProducts)
          && voucher.voucherGroup.includedProducts.indexOf(item.variant.id) > -1;

        if (!included) {
          return acc;
        }

        return acc + item.quantity;
      }, 0);

      // const giftInCart = this._orderedItems.find((item: IOrder) => item.variant.id === gift.id);
      const quantityToBeAdded = quantity;
      this.addToCart(gift, undefined, quantityToBeAdded);
    }

    this._dynamicVouchers.push(voucher);
    this.save();
  }

  @action
  public clearVoucher() {
    this._dynamicVouchers = observable([]);
    this._staticVouchers = observable([]);
    this.save();
  }

  public getStockQuantity(variant: GQL.IProductVariant) {
    const prices = variant.prices;
    if (!Array.isArray(prices)) {
      return null;
    }

    const currencyCode = CountryService.GetInstance().currency.id;
    const price = prices.find((p: any) => p.currencyId === currencyCode);

    if (!price) {
      return null;
    }

    return price.stockAmount;
  }

  public getMaxTypeQuantity(variant: string | GQL.IProductVariant): IMaxQuantityType | null {
    if ('string' === typeof variant) {
      const item = this.findVariant(variant);
      if (!item) {
        return null;
      }

      variant = item.variant;
    }

    const maxStockQuantity = this.getStockQuantity(variant);
    const maxPurchaseQuantity = variant.product && 'number' === typeof variant.product.purchaseLimitPerPurchase
      ? variant.product.purchaseLimitPerPurchase
      : null;

    let maxQuantity: null | IMaxQuantityType = null;
    const limitSource: IMaxQuantityType[] = [
      {
        value: maxStockQuantity,
        type: 'STOCKQUANTITY',
      }, {
        value: maxPurchaseQuantity,
        type: 'PURCHASELIMITQUANTITY',
      }
    ];
    for (const maxQ of limitSource) {
      if ('number' === typeof maxQ.value && (null === maxQuantity || ('number' === typeof maxQuantity.value && maxQ.value < maxQuantity.value))) {
        maxQuantity = maxQ;
      }
    }

    return maxQuantity;
  }

  public getMaxQuantity(variant: string | GQL.IProductVariant): number | null {
    const maxQuantity = this.getMaxTypeQuantity(variant);
    if (!maxQuantity) {
      return null;
    }

    return maxQuantity.value;
  }

  @action
  public addToCart(variant: GQL.IProductVariant, params?: { GTMItemListName?: string }, quantity: number = 1) {
    const orderIndex = this._orderedItems.findIndex(o => o.variant.id == variant.id);
    const maxQuantity = this.getMaxQuantity(variant);

    if (orderIndex > -1) {
      if ((maxQuantity && (this._orderedItems[orderIndex].quantity + quantity) > maxQuantity)) {
        this._orderedItems[orderIndex].quantity = maxQuantity;
      } else {
        this._orderedItems[orderIndex].quantity += Math.min(quantity, 999);
      }
    } else {
      this._orderedItems = observable([
        ...this._orderedItems,
        {
          variant,
          quantity: (maxQuantity && quantity > maxQuantity) ? maxQuantity : quantity,
          GTMItemListName: params && params.GTMItemListName || undefined
        }
      ]);
    }

    this.save();

    GoogleTagManagerService.addToOrRemoveFromBasket(this._orderedItems);
  }

  @action
  public updateQuantity(variantId: string, quantity: number) {
    if (isNaN(quantity)) {
      return;
    }

    if (quantity <= 0) {
      this.removeItemCompletelyFromCart(variantId);
    }

    const orderIndex = this._orderedItems.findIndex(o => o.variant.id == variantId);
    if (orderIndex == -1) {
      return;
    }
    if (quantity <= 0) {
      this.removeItemCompletelyFromCart(variantId);
    }

    const variant = toJS(this._orderedItems[orderIndex].variant);
    const maxQuantity = this.getMaxTypeQuantity(variant);
    const useMaxQuantity = (maxQuantity && 'number' === typeof maxQuantity.value && quantity > maxQuantity.value);

    if (quantity > 999) {
      quantity = 999;
    }

    const newOrderItems = [...this._orderedItems];
    newOrderItems[orderIndex].quantity = useMaxQuantity && maxQuantity && 'number' === typeof maxQuantity.value ? maxQuantity.value : quantity;

    this._orderedItems = observable(newOrderItems);

    if (useMaxQuantity) {
      FlashStore.GetInstance().showMessage(maxQuantity?.type === 'PURCHASELIMITQUANTITY' ? `Only ${maxQuantity.value} pcs can be ordered` : `We have only ${maxQuantity?.value} pcs in stock`, 'error');
    }

    this.save();

    GoogleTagManagerService.addToOrRemoveFromBasket(this._orderedItems);
  }

  @action
  public removeItemQuantityFromCart(variantId: string, quantity: number = 1) {
    const orderIndex = this._orderedItems.findIndex(o => o.variant.id == variantId);
    if (orderIndex == -1) {
      return;
    }

    this._orderedItems[orderIndex].quantity -= quantity;

    if (this._orderedItems[orderIndex].quantity <= 0) {
      this._orderedItems = observable(this._orderedItems.filter(o => o.variant.id != variantId));
    }

    this.save();

    GoogleTagManagerService.addToOrRemoveFromBasket(this._orderedItems);
  }

  @action
  public removeItemCompletelyFromCart(variantId: string) {
    this._orderedItems = observable(this._orderedItems.filter(o => o.variant.id != variantId));

    this.save();

    GoogleTagManagerService.addToOrRemoveFromBasket(this._orderedItems);
  }

  @action
  public removeEverything() {
    this._orderedItems = observable([]);
    this._delivery = observable.box(null);
    this._deliveryInsuranceLimit = observable.box(null);
    this._payment = observable.box(null);
    this._customerData = observable({});
    // this._dynamicVouchers = this._dynamicVouchers.filter(v => v.title == undefined);
    this._dynamicVouchers = observable([]);

    localStorage.removeItem('cart');
  }

  /**
   * Vyhledá položku v košíku podle varianty
   * 
   * @param variantId
   */
  public findVariant(variantId: string) {
    return this._orderedItems.find((item) => item.variant.id === variantId);
  }

  public getAllVariantIds() {
    return this._orderedItems.map(o => o.variant.id);
  }

  public getTotalPriceForVariant(variantId: string, calculateVouchers: boolean) {
    const order = this._orderedItems.find(o => o.variant.id == variantId);
    if (order == undefined) {
      return 0;
    }

    return this.getPriceForVariant(variantId, calculateVouchers) * order.quantity;
  }

  public getTotalPriceWithoutVatForVariant(variantId: string, calculateVouchers: boolean) {
    const order = this._orderedItems.find(o => o.variant.id == variantId);
    if (order == undefined) {
      return 0;
    }

    return this.getPriceWithoutVatForVariant(variantId, calculateVouchers) * order.quantity;
  }

  public getPriceForVariant(variantId: string, calculateVouchers: boolean) {
    const order = this._orderedItems.find(o => o.variant.id == variantId);
    const { VATrate, currency } = CountryService.GetInstance();
    const { customerGroupId } = AuthService.GetInstance();

    if (!order || !currency) {
      return 0;
    }

    const item = {
      variantId: order.variant.id,
      price: findFinalPrice(order.variant, false, VATrate),
      recommendedPrice: findFinalPrice(order.variant, true, VATrate),
      tags: order.variant.product.tags || [''], // todo: odstraň ['']
    };

    const vouchers = calculateVouchers
      ? this.vouchers
        .filter(v => !isCoupon(v)) // remove coupons from list of discounts
        .map(v => ({ ...v.voucherGroup, voucherDiscountAmount: CartService.getDirectVoucherDiscountValue(v) })) as any
      : [];

    const priceOfSingle = calculatePriceOfItemByVouchers(
      item,
      vouchers,
      this.totalPriceBeforeDiscounts(this._orderedItems),
      currency.id,
      Array.isArray(customerGroupId) ? customerGroupId : (customerGroupId || ''),
      this.orderItems,
    );

    return Math.round(priceOfSingle * 100) / 100;
  }

  public getPriceWithoutVatForVariant(variantId: string, calculateVouchers: boolean) {
    const order = this._orderedItems.find(o => o.variant.id == variantId);
    const { currency } = CountryService.GetInstance();
    const { customerGroupId } = AuthService.GetInstance();

    if (order == undefined) {
      return 0;
    }

    const item = {
      variantId: order.variant.id,
      price: findFinalPrice(order.variant, false),
      recommendedPrice: findFinalPrice(order.variant, true),
      tags: order.variant.product.tags || [''], // todo: odstraň ['']
    };

    const vouchers = calculateVouchers
      ? this.vouchers
        .filter(v => v.voucherGroup && (!v.voucherGroup.customerIds) && (!v.customerId))
        .map(v => ({ ...v.voucherGroup, voucherDiscountAmount: CartService.getDirectVoucherDiscountValue(v) })) as any
      : [];

    const priceOfSingle = calculatePriceOfItemByVouchers(
      item,
      vouchers,
      this.totalPriceBeforeDiscounts(this._orderedItems),
      currency.id,
      Array.isArray(customerGroupId) ? customerGroupId : (customerGroupId || ''),
      this.orderItems,
    );

    return priceOfSingle;
  }

  public getQuantityForVariant(variantId: string) {
    const order = this._orderedItems.find(o => o.variant.id == variantId);

    if (order == undefined) {
      return 0;
    }

    return order.quantity;
  }

  public getGTMItemListName(variantId: string) {
    const order = this._orderedItems.find(o => o.variant.id == variantId);

    if (!order) {
      return undefined;
    }

    return order.GTMItemListName;
  }

  private static getDirectVoucherDiscount(voucher: GQL.IVoucher) {
    const currencyCode = CountryService.GetInstance().currencyCode;

    // Tady nesmí být Array.isArray(voucher.amountValue), protože voucher.amountValue není Array, ale mobx ObservableArray
    const directVoucherDiscount = voucher.amountValue &&
      voucher.amountValue.find((p: { currencyCode: string; currencyId: string; priceWithout: number | null; recommendedPriceWithout: number | null; }) => {
        return (p.currencyCode === currencyCode);
      }) || null;

    return directVoucherDiscount;
  }

  // Pokud sleva není definovaná, nesmí vracet číslo
  public static getDirectVoucherDiscountValue(voucher: GQL.IVoucher) {
    const directVoucherDiscount = CartService.getDirectVoucherDiscount(voucher);
    if (!directVoucherDiscount) {
      return undefined;
    }
    return directVoucherDiscount.priceWithout || 0;
  }

  @computed
  public get individualCustomerVoucherDiscount() {
    const coupons = this.vouchers.filter(v => isCoupon(v) && haveValidCustomer(v));
    if (coupons.length < 1) {
      return 0;
    }

    const totalPrice = this.totalPrice;
    let uncoveredTotalPrice = totalPrice;

    let discount = 0;
    for (const voucher of coupons) {
      if (!voucher.voucherGroup) {
        continue;
      }

      const directVoucherDiscount = CartService.getDirectVoucherDiscount(voucher);
      const voucherDiscount = Math.min(((directVoucherDiscount)
        ? Math.max((directVoucherDiscount.priceWithout || 0), 0)
        : totalPrice * voucher.voucherGroup.amount / 100), uncoveredTotalPrice);

      discount += voucherDiscount;
      uncoveredTotalPrice -= voucherDiscount;
    }

    return discount;
  }

  @computed
  public get individualCustomerVoucherDiscountWithoutVat() {
    const VATrate = CountryService.GetInstance().VATrate;

    if (VATrate && (VATrate > 0)) {
      return this.individualCustomerVoucherDiscount / (100 + VATrate) * 100;
    }

    return this.individualCustomerVoucherDiscount;
  }

  @action toggleRecycledBox() {
    this._recycledBox = !this._recycledBox;
    this.save();
  }

  @computed get recycledBox() {
    return this._recycledBox;
  }

  // doprava
  @action
  public setDelivery(delivery: GQL.IDeliveryMethod | null) {
    this._delivery = observable.box(delivery);

    this.save();
  }

  @computed
  public get delivery() {
    const delivery = this._delivery.get();
    return (delivery ? delivery : null);
  }

  @computed
  public get deliveryId() {
    const delivery = this._delivery.get();
    if (delivery) {
      return delivery.id;
    }

    return null;
  }

  @computed
  public get deliveryPrice() {
    const { VATrate, countryCode } = CountryService.GetInstance();
    const delivery = this._delivery.get();

    if (delivery && delivery.unknownPrice === true) {
      return null;
    }

    if (delivery) {
      const prices = getDeliveryMethodPrices(
        delivery,
        CartService.GetInstance().totalWeightForShipping
      );

      const deliveryVATRate = countryCode === 'us' ? 0 : VATrate;
      return findPrice(
        prices,
        false,
        deliveryVATRate,
        delivery,
      );
    }

    return undefined;
  }

  // pojištění dopravy
  @action
  public setDeliveryInsuranceLimit(deliveryInsuranceLimit: GQL.IDeliveryMethodInsuranceLimit | null) {
    this._deliveryInsuranceLimit = observable.box(deliveryInsuranceLimit);

    this.save();
  }

  @computed
  public get deliveryInsuranceLimit() {
    const deliveryInsuranceLimit = this._deliveryInsuranceLimit.get();
    return (deliveryInsuranceLimit ? deliveryInsuranceLimit : null);
  }

  @computed
  public get deliveryInsurancePrice() {
    const { VATrate } = CountryService.GetInstance();

    const deliveryInsuranceLimit = this._deliveryInsuranceLimit.get();
    if (deliveryInsuranceLimit) {
      return findPrice(deliveryInsuranceLimit.prices, false, VATrate, deliveryInsuranceLimit);
    }

    return null;
  }

  // platba
  @action
  public setPayment(payment: GQL.IPaymentMethod | null) {
    this._payment = observable.box(payment);

    this.save();
  }

  @computed
  public get payment() {
    const payment = this._payment.get();
    return (payment ? payment : null);
  }

  @computed
  public get paymentId() {
    const payment = this._payment.get();
    if (payment) {
      return payment.id;
    }

    return null;
  }

  @computed
  public get paymentPrice() {
    const { VATrate } = CountryService.GetInstance();

    const payment = this._payment.get();
    if (payment) {
      return findPrice(payment.prices, false, VATrate, payment);
    }

    return null;
  }

  // objekt cen pro zobrazení v košíku
  @computed
  public get prices() {
    const totalPrice = this.totalPrice;

    // cena dopravy, ověření zda není doprava zdarma, např. při objednávce nad X peněz
    const deliveryPrice = this.deliveryId
      ? this.isFreeDelivery(this.deliveryId)
        ? 0
        : this.deliveryPrice
      : this.deliveryPrice;

    // výstup je object
    return {
      Total: roundPrice(totalPrice),
      Payment: this.paymentPrice ? roundPrice(this.paymentPrice) : this.paymentPrice,
      Shipment: deliveryPrice ? roundPrice(deliveryPrice) : deliveryPrice,
      DeliveryInsurance: roundPrice(this.deliveryInsurancePrice),
      Discount: this.individualCustomerVoucherDiscount > 0 ? this.individualCustomerVoucherDiscount : undefined,
    };
  }

  // osobni data
  @action
  public setCustomerData(customerData: any) {
    this._customerData = observable({ ...customerData });

    this.save();
  }

  @action
  public updateCustomerData(data: any) {
    this._customerData = observable({ ...this._customerData, ...data });

    this.save();
  }

  @computed
  public get customerData() {
    return this._customerData;
  }

  @computed
  public get invoiceCountryByCustomer() {
    return (this._customerData as any)._InvoiceAddressCountrySelectedByCustomer;
  }

  @computed
  public get deliveryCountryByCustomer() {
    return (this._customerData as any)._DeliveryAddressCountrySelectedByCustomer;
  }

  public doCountryCurrenciesMatch() {
    const cartService = CartService.GetInstance();
    const { currency } = CountryService.GetInstance();
    const anotherDeliveryCountry = (cartService.customerData as any).anotherDeliveryAddressCountry;
    const deliveryCountry = anotherDeliveryCountry
      ? cartService.deliveryCountryByCustomer
      : cartService.invoiceCountryByCustomer;

    if (deliveryCountry && deliveryCountry.currency && currency) {
      const res = deliveryCountry.currency.id === currency.id;

      if (!res) {
        FlashStore.GetInstance().showMessage('The country you selected for shipping doesn’t match the region of the website.', 'error');
      }
      
      return res;
    }

    return null;
  }

  /**
   * Uložení košíku do localStorage
   */
  private save() {
    if (this._orderedItems.length === 0) {
      this._staticVouchers = observable([]);
      this._dynamicVouchers = observable([]);
    }

    const values = {
      items: this._orderedItems,
      delivery: this._delivery,
      recycledBox: this._recycledBox,
      deliveryInsuranceLimit: this._deliveryInsuranceLimit,
      payment: this._payment,
      customerData: this._customerData,
      vouchers: this._dynamicVouchers,
    };

    if (canUseWindow()) {
      localStorage.setItem('cart', JSON.stringify(values));
    }
  }

  private async initialize() {
    // data z localStorage
    const tempCart = canUseWindow() ? localStorage.getItem('cart') : undefined;
    const tempCartJson = tempCart ? JSON.parse(tempCart) : {};
    // položky košíku z localStorage
    tempCartJson && tempCartJson.items
      ? (this._orderedItems = observable(tempCartJson.items))
      : (this._orderedItems = observable([]));

    // doprava z localStorage
    tempCartJson && tempCartJson.delivery
      ? (this._delivery = observable.box(tempCartJson.delivery))
      : (this._delivery = observable.box(null));
    // pojištění dopravy
    tempCartJson && tempCartJson.deliveryInsuranceLimit
      ? (this._deliveryInsuranceLimit = observable.box(tempCartJson.deliveryInsuranceLimit))
      : (this._deliveryInsuranceLimit = observable.box(null));
    // platba z localStorage
    tempCartJson && tempCartJson.payment
      ? (this._payment = observable.box(tempCartJson.payment))
      : (this._payment = observable.box(null));

    tempCartJson && tempCartJson.recycledBox
      ? (this._recycledBox = tempCartJson.recycledBox)
      : (this._recycledBox = false);

    // osobni data z localStorage
    tempCartJson && tempCartJson.customerData
      ? (this._customerData = observable(tempCartJson.customerData))
      : (this._customerData = observable({}));
    // dynamické vouchery, z kódu
    tempCartJson && tempCartJson.vouchers
      ? (this._dynamicVouchers = observable(tempCartJson.vouchers))
      : (this._dynamicVouchers = observable([]));
    // z db zjistím seznam statických voucherů
    this._staticVouchers = await fetchStaticDiscounts();
    this.validateBasketStatus();
  }

  private constructor() {
    this._staticVouchers = [];
    this.initialize();
  }

  async validateBasketStatus() {
    const client = ApolloClientFactory.GetClient();
    const { currency } = CountryService.GetInstance();
    const ids = this.getAllVariantIds();

    if (!currency) {
      return false;
    }

    if (!Array.isArray(ids) || ids.length < 1) {
      return true;
    }

    const response = await client.query({
      query: GetFullVariantsQuery,
      variables: { ids, currencyId: currency.id },
      fetchPolicy: 'network-only'
    });

    const variants = response && response.data && response.data.variants;
    if (!Array.isArray(variants) || variants.length < 1) {
      return true;
    }

    let res = true;
    for (const variantId of ids) {
      const variant = (variants || []).find(v => v.id == variantId);
      // NOTICE: this can cause bug when somebody change the name
      const unavailable = variant && variant.product && variant.product.availability && (variant.product.availability.name === PRODUCT_STATUS_UNAVAILABLE);
      if (!variant || unavailable) {
        FlashStore.GetInstance().showMessage('Some products are unavailable. These products have been removed from your cart.', 'error');
        this.removeItemCompletelyFromCart(variantId);
        res = res && false;
      }
    }

    return res;
  }
}



// WEBPACK FOOTER //
// ./src/services/CartService.ts